import { visitorAtMidnight } from "./readings/visitor-at-midnight";
import { theReconciliation } from "./readings/the-reconciliation";
import { theCostOfBeingClear } from "./readings/the-cost-of-being-clear";
import { anyOtherBusiness } from "./readings/any-other-business";
import { theLastKey } from "./readings/the-last-key";
import initialLessonLexiconJson from "./generated/lesson-lexicons/visitor-at-midnight.json";
import type { IntentDefinition, LessonLexicon, Reading, ReadingIntent } from "./types";
import { assertValidCatalog } from "./validation";

export const READING_INTENTS: IntentDefinition[] = [
  {
    id: "complex-thought",
    label: "Express complex thoughts clearly",
    description: "Distil a layered idea without flattening it.",
    outcomes: ["precise thought", "conceptual distillation"],
    rhetoricalMoves: ["contrast", "compression", "reframing"],
  },
  {
    id: "tactful-confidence",
    label: "Speak with tactful confidence",
    description: "Carry a boundary or disagreement without aggression.",
    outcomes: ["tactful firmness", "composed authority"],
    rhetoricalMoves: ["precise assertion", "refusal", "concession"],
  },
  {
    id: "difficult-vocabulary",
    label: "Build difficult vocabulary",
    description: "Meet advanced words that remain useful beyond the page.",
    outcomes: ["lexical command", "precise thought"],
    rhetoricalMoves: ["contrast", "qualification"],
  },
  {
    id: "implication",
    label: "Understand implication",
    description: "Notice what a sentence makes present without stating it.",
    outcomes: ["emotional precision", "inference"],
    rhetoricalMoves: ["implication", "understatement", "strategic silence"],
  },
  {
    id: "rhythm",
    label: "Develop rhythm",
    description: "Feel how cadence changes authority and memory.",
    outcomes: ["cadence", "memorable expression"],
    rhetoricalMoves: ["parallelism", "compression", "controlled release"],
  },
  {
    id: "force",
    label: "Use language with more force",
    description: "Make the sentence carry weight without merely raising its volume.",
    outcomes: ["composed authority", "precise thought"],
    rhetoricalMoves: ["precise assertion", "contrast", "reframing"],
  },
];

export const READINGS: Reading[] = assertValidCatalog([
  visitorAtMidnight,
  theReconciliation,
  theCostOfBeingClear,
  anyOtherBusiness,
  theLastKey,
]);

export const READING_BY_ID = new Map(READINGS.map((reading) => [reading.id, reading]));
export const INITIAL_LESSON_LEXICON = initialLessonLexiconJson as LessonLexicon;

const LESSON_LEXICON_LOADERS: Record<string, () => Promise<LessonLexicon>> = {
  "any-other-business": () => import("./generated/lesson-lexicons/any-other-business.json").then((module) => module.default as LessonLexicon),
  "the-cost-of-being-clear": () => import("./generated/lesson-lexicons/the-cost-of-being-clear.json").then((module) => module.default as LessonLexicon),
  "the-last-key": () => import("./generated/lesson-lexicons/the-last-key.json").then((module) => module.default as LessonLexicon),
  "the-reconciliation": () => import("./generated/lesson-lexicons/the-reconciliation.json").then((module) => module.default as LessonLexicon),
};

export function loadLessonLexicon(readingId: string) {
  if (readingId === "visitor-at-midnight") return Promise.resolve(INITIAL_LESSON_LEXICON);
  return LESSON_LEXICON_LOADERS[readingId]?.() || Promise.resolve(null);
}

export function findDictionaryEntry(word: string, lexicon: LessonLexicon | null) {
  return lexicon?.entries[word.toLocaleLowerCase().replace(/[’']/g, "'").trim()];
}

export function readingTokenIds(reading: Reading) {
  const wordPattern = /^\p{L}+(?:[’'-]\p{L}+)*$/u;
  return reading.segments.flatMap((segment) =>
    segment.text
      .split(/(\p{L}+(?:[’'-]\p{L}+)*)/gu)
      .map((part, index) => (wordPattern.test(part) ? `${segment.id}-t${index}` : null))
      .filter((tokenId): tokenId is string => Boolean(tokenId)),
  );
}

export function recommendReadings(intentId: ReadingIntent, currentReadingId?: string) {
  const intent = READING_INTENTS.find((candidate) => candidate.id === intentId);
  if (!intent) return [];
  return READINGS
    .map((reading) => {
      const outcomeMatches = reading.taxonomy.outcomes.filter((value) => intent.outcomes.includes(value));
      const moveMatches = reading.taxonomy.rhetoricalMoves.filter((value) => intent.rhetoricalMoves.includes(value));
      const matchedCraft = [...outcomeMatches, ...moveMatches].slice(0, 3);
      return {
        reading,
        score: outcomeMatches.length * 3 + moveMatches.length * 2,
        reason: matchedCraft.length
          ? `Chosen for ${matchedCraft.join(", ")}. ${reading.whyItFits}`
          : "",
      };
    })
    .filter((candidate) => candidate.score > 0 && candidate.reading.id !== currentReadingId)
    .sort((a, b) => b.score - a.score || a.reading.title.localeCompare(b.reading.title))
    .slice(0, 4);
}

export function findWordInsight(word: string) {
  for (const reading of READINGS) {
    const entry = reading.words[word];
    if (entry) return entry;
  }
  return undefined;
}
